import {
  CurrentPlanInOfferingMode,
  Experiments,
  PackagePickerType,
  PaymentCycle,
  ProductChangeType,
  ProductFamily,
} from 'enums';
import { AppStore } from 'stores/App.store';
import { AdvancedPlansProductData } from 'types';
import { getProductFamilyIsInSale } from 'utils';

export class AdvancedPlansProductModel {
  private readonly productData: AdvancedPlansProductData;
  isProductInSale: boolean = false;
  isCurrentPlanProduct: boolean;
  isCurrentPlanSameCode: boolean;
  isCurrentPlanSameProductFamily: boolean;

  constructor(
    data: AdvancedPlansProductData,
    private readonly appStore: AppStore,
  ) {
    const {
      currentPlanInOfferingMode,
      appContext: { currentPlanDetails, isDynamicOffering, experiments },
    } = appStore;
    this.productData = data;
    this.isCurrentPlanProduct =
      currentPlanInOfferingMode ===
        CurrentPlanInOfferingMode.ProductFullMatch &&
      (data.id === currentPlanDetails!.planId ||
        (isDynamicOffering && data.guid === currentPlanDetails!.guid));

    this.isCurrentPlanSameCode =
      appStore.currentPlanInOfferingMode ===
        CurrentPlanInOfferingMode.ProductPartialMatch &&
      data.productCode === currentPlanDetails!.productCode;

    this.isCurrentPlanSameProductFamily =
      experiments[Experiments.ChangePlanProductFamilyFix] === 'true' &&
      appStore.currentPlanInOfferingMode ===
        CurrentPlanInOfferingMode.ProductPartialMatch &&
      data.productFamilyId === currentPlanDetails!.productFamilyId;

    this.isProductInSale = getProductFamilyIsInSale(data.productFamily);
  }

  private readonly findCycle = (cycle: PaymentCycle) =>
    this.productData.cycles.find((c) => c.cycleId === cycle);

  getYearlyFullPrice = () => {
    const yearly = this.findCycle(PaymentCycle.YEARLY);
    return yearly ? yearly.monthlyFullPrice : NaN;
  };

  getFirstCycleYearlyMonthly = () => {
    const yearly = this.findCycle(PaymentCycle.YEARLY);
    return yearly ? yearly.firstPeriodPriceMonthly : NaN;
  };

  getYearlyActualPrice = () => {
    const yearly = this.findCycle(PaymentCycle.YEARLY);
    return yearly ? yearly.monthlyActualPrice : NaN;
  };

  getActualPriceByCycle = (cycle: PaymentCycle) => {
    const nYearly = this.findCycle(cycle);
    return nYearly ? nYearly.monthlyActualPrice : this.getYearlyActualPrice();
  };

  getFullPriceByCycle = (cycle: PaymentCycle) => {
    const nYearly = this.findCycle(cycle);
    return nYearly ? nYearly.monthlyActualPrice : this.getYearlyFullPrice();
  };

  isSelected = () => {
    const { isChangePlan, getDefaultCurrentProductForPlanType } = this.appStore;
    return (
      getDefaultCurrentProductForPlanType.id === this.productData.id &&
      (!isChangePlan ||
        this.productData.productChangeType === ProductChangeType.Upgrade)
    );
  };

  getPriceToDisplay = () =>
    this.getFirstCycleYearlyMonthly() || this.getYearlyActualPrice();

  isPurchaseEnabled = () => {
    const {
      isSecondUpgrade,
      experiments,
      isChangePlan,
      shouldRenderWixStudioPackagePicker,
      isSunriseV2Enabled,
      appContext,
    } = this.appStore;

    const unifiedChangePlanPhase1Enabled =
      experiments[Experiments.UnifiedChangePlanPhase1] === 'true';

    const isUnifiedChangePlan = unifiedChangePlanPhase1Enabled && isChangePlan;

    // Same logic as in the Client.tsx file
    const isSunrisePackagePicker =
      appContext.packagePickerType !== PackagePickerType.Sandbox &&
      !shouldRenderWixStudioPackagePicker &&
      isSunriseV2Enabled;

    if (!this.productData.hasPurchasableCycles) {
      return false;
    }

    if (isSecondUpgrade) {
      return true;
    }

    const isCurrentPlanOrSameFamily =
      this.isCurrentPlanProduct || this.isCurrentPlanSameProductFamily;

    if (isCurrentPlanOrSameFamily) {
      if (isUnifiedChangePlan && isSunrisePackagePicker) {
        return this.getProductChangeType() === ProductChangeType.Upgrade;
      }
      return false;
    }

    return true;
  };
  getId = () => this.productData.id;
  getGuid = () => this.productData.guid;
  getBaseProduct = () => this.productData;
  getCode = () => this.productData.productCode;
  getHatLabel = () => {
    const { offeringHatData, showHatFromDealer } = this.appStore;
    if (showHatFromDealer()) {
      const guid = this.getGuid();
      if (
        offeringHatData.productFamilies?.includes(
          this.productData.productFamily,
        ) ||
        (guid && offeringHatData.productIds?.includes(guid))
      ) {
        return offeringHatData.text;
      }
    } else {
      return this.productData.hatKey;
    }
  };
  getPopular = () => {
    const { offeringHatData, showHatFromDealer } = this.appStore;
    if (showHatFromDealer()) {
      const guid = this.getGuid();
      return (
        offeringHatData.productFamilies?.includes(
          this.productData.productFamily,
        ) ||
        (guid && offeringHatData.productIds?.includes(guid))
      );
    } else {
      return this.productData.popular;
    }
  };
  getType = () => this.productData.type;
  getName = () => this.productData.productName;
  getDescription = () => this.productData.description;
  shouldShowAdsNote = () => this.productData.shouldShowAdsNote;
  isEnterprise = () =>
    this.productData.productFamily === ProductFamily.Enterprise;
  getProductChangeType = () => this.productData.productChangeType;
  getDiscountPercent = () => this.productData.firstPeriodDiscount;
  getSaleDiscountPercentage = () => this.productData.saleDiscountPercentage;
  isNonPurchasable = () => this.productData.productName === 'Free';
}
